import React, { useMemo } from 'react';
import { ConvictionWidgetInstanceConfig, ConvictionDataContent, ConvictionCategory, QADataContent, ImageRepositoryDataContent } from '../../../../../types';
import { WidgetEmptyState } from '../../../design';

type DashboardDataContent = QADataContent | ConvictionDataContent | ImageRepositoryDataContent;

interface ConvictionWidgetRendererProps {
  config: ConvictionWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: DashboardDataContent) => void;
  readOnly?: boolean;
}

const ConvictionWidgetRenderer: React.FC<ConvictionWidgetRendererProps> = ({
  config,
  dashboardData = {},
  onDataChange,
  readOnly = false,
}) => {
  const convictionData = dashboardData[config.dataKey] as ConvictionDataContent | undefined;

  const calculatedOverallScore = useMemo(() => {
    if (!convictionData?.categories || convictionData.categories.length === 0) return null;

    const categoriesWithScores = convictionData.categories.filter(c => c.score !== undefined);
    if (categoriesWithScores.length === 0) return null;

    const totalWeight = categoriesWithScores.reduce((sum, c) => sum + (c.weight || 1), 0);
    const weightedSum = categoriesWithScores.reduce((sum, c) => {
      const weight = c.weight || 1;
      const score = c.score || 0;
      const minScore = c.minScore || 1;
      const maxScore = c.maxScore || 10;
      const normalizedScore = ((score - minScore) / (maxScore - minScore)) * 100;
      return sum + (normalizedScore * weight);
    }, 0);

    return Math.round(weightedSum / totalWeight);
  }, [convictionData]);

  const getScoreColor = (percentage: number) => {
    if (percentage <= 33) return { bg: 'bg-danger-500', text: 'text-danger-600', light: 'bg-danger-50', border: 'border-danger-200' };
    if (percentage <= 66) return { bg: 'bg-warning-500', text: 'text-warning-600', light: 'bg-warning-50', border: 'border-warning-200' };
    return { bg: 'bg-success-500', text: 'text-success-600', light: 'bg-success-50', border: 'border-success-200' };
  };

  const getScoreLabel = (percentage: number) => {
    if (percentage <= 25) return 'Very Low';
    if (percentage <= 50) return 'Low';
    if (percentage <= 75) return 'High';
    return 'Very High';
  };

  // Inline validation for a category — mirrors the registry validator
  // (conviction/index.ts): required-but-unscored, or scored out of bounds.
  const getCategoryError = (category: ConvictionCategory): string | null => {
    const unscored = category.score === undefined || category.score === null;
    const required = category.required !== false;
    if (unscored) return required ? 'A score is required' : null;
    const min = category.minScore ?? 1;
    const max = category.maxScore ?? 10;
    if (category.score! < min || category.score! > max) {
      return `Score must be between ${min} and ${max}`;
    }
    return null;
  };

  const handleScoreChange = (categoryId: string, newScore: number) => {
    if (!convictionData || !onDataChange) return;

    const updatedCategories = convictionData.categories.map((cat) =>
      cat.id === categoryId ? { ...cat, score: newScore } : cat
    );

    onDataChange(config.dataKey, {
      ...convictionData,
      categories: updatedCategories,
    });
  };

  const handleCommentChange = (categoryId: string, newComment: string) => {
    if (!convictionData || !onDataChange) return;

    const updatedCategories = convictionData.categories.map((cat) =>
      cat.id === categoryId ? { ...cat, comment: newComment } : cat
    );

    onDataChange(config.dataKey, {
      ...convictionData,
      categories: updatedCategories,
    });
  };

  const handleOverallCommentChange = (newComment: string) => {
    if (!convictionData || !onDataChange) return;

    onDataChange(config.dataKey, {
      ...convictionData,
      overallComment: newComment,
    });
  };

  // No data key configured
  if (!config.dataKey) {
    return (
      <WidgetEmptyState
        icon={
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        }
        title="No data key configured"
      />
    );
  }

  // No data found
  if (!convictionData || !convictionData.categories || convictionData.categories.length === 0) {
    return (
      <WidgetEmptyState
        icon={
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01" />
          </svg>
        }
        title="No tags configured"
        description="Configure in workflow settings"
      />
    );
  }

  const overallColors = calculatedOverallScore !== null ? getScoreColor(calculatedOverallScore) : null;

  return (
    <div className="h-full overflow-auto bg-surface">
      {/* Header */}
      <div className="sticky top-0 bg-surface border-b border-neutral-100 px-4 py-3 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-semibold text-neutral-800">
              {convictionData.title || 'Conviction Score'}
            </span>
          </div>
          {calculatedOverallScore !== null && (
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${overallColors?.light} ${overallColors?.border} border`}>
              <span className={`text-2xl font-bold ${overallColors?.text}`}>
                {calculatedOverallScore}%
              </span>
            </div>
          )}
        </div>
        {convictionData.description && (
          <p className="text-xs text-neutral-500 mt-1">{convictionData.description}</p>
        )}
      </div>

      {/* Categories */}
      <div className="p-4 space-y-3">
        {convictionData.categories.map((category) => {
          const minScore = category.minScore || 1;
          const maxScore = category.maxScore || 10;
          const currentScore = category.score || minScore;
          const percentage = ((currentScore - minScore) / (maxScore - minScore)) * 100;
          const colors = getScoreColor(percentage);
          const label = config.showLabels ? getScoreLabel(percentage) : null;
          const categoryError = !readOnly ? getCategoryError(category) : null;

          return (
            <div
              key={category.id}
              className={`p-4 rounded-lg border bg-neutral-50/50 ${categoryError ? 'border-danger-500' : 'border-neutral-200'}`}
            >
              {/* Category Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-neutral-800 text-sm">{category.name}</span>
                    {category.weight && category.weight !== 1 && (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-neutral-200 text-neutral-600">
                        {category.weight}x
                      </span>
                    )}
                  </div>
                  {category.description && (
                    <p className="text-xs text-neutral-500 mt-0.5">{category.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 ml-3">
                  <span className={`text-xl font-bold ${colors.text}`}>{currentScore}</span>
                  {label && (
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${colors.light} ${colors.text} uppercase`}>
                      {label}
                    </span>
                  )}
                </div>
              </div>

              {/* Slider */}
              <div className="relative">
                <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${colors.bg} transition-all duration-200 rounded-full`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
                {!readOnly && (
                  <input
                    type="range"
                    min={minScore}
                    max={maxScore}
                    value={currentScore}
                    onChange={(e) => handleScoreChange(category.id, parseInt(e.target.value, 10))}
                    className="absolute inset-0 w-full h-2 opacity-0 cursor-pointer"
                  />
                )}
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-[10px] text-neutral-400">{minScore}</span>
                <span className="text-[10px] text-neutral-400">{maxScore}</span>
              </div>

              {categoryError && (
                <p className="text-xs text-danger-500 mt-1.5 font-medium">{categoryError}</p>
              )}

              {/* Comment */}
              {config.allowComments && (
                <div className="mt-3">
                  {!readOnly ? (
                    <input
                      type="text"
                      value={category.comment || ''}
                      onChange={(e) => handleCommentChange(category.id, e.target.value)}
                      placeholder="Add note..."
                      className="w-full px-3 py-2 text-xs border border-neutral-200 rounded-md bg-surface"
                    />
                  ) : category.comment ? (
                    <p className="text-xs text-neutral-500 italic bg-surface px-3 py-2 rounded-md border border-neutral-100">
                      {category.comment}
                    </p>
                  ) : null}
                </div>
              )}
            </div>
          );
        })}

        {/* Overall Comment */}
        {config.allowComments && (
          <div className="pt-3 border-t border-neutral-200">
            <label className="text-xs font-medium text-neutral-600 mb-2 block">
              Overall Rationale
            </label>
            {!readOnly ? (
              <textarea
                value={convictionData.overallComment || ''}
                onChange={(e) => handleOverallCommentChange(e.target.value)}
                placeholder="Add your overall conviction rationale..."
                className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-md bg-surface resize-none"
                rows={2}
              />
            ) : convictionData.overallComment ? (
              <p className="text-sm text-neutral-600 bg-neutral-50 px-3 py-2 rounded-md border border-neutral-100">
                {convictionData.overallComment}
              </p>
            ) : (
              <p className="text-xs text-neutral-400 italic">No rationale provided</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConvictionWidgetRenderer;
